/**
 * @file MeditationStep.tsx
 *
 * @description Step component for general meditation in the Miracle Flow.
 * Provides a timer-based meditation session for centering and relaxation.
 *
 * @module miracle-flow
 *
 * @domain productivity
 *
 */

import { Brain } from "lucide-react";
import React from "react";

import { Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { Timer } from "../../atoms/Timer";

/**
 * Props for MeditationStep component
 */
export interface MeditationStepProperties {
  className?: string;
  duration: number;
  isActive: boolean;
  onComplete: () => void;
  onStart: () => void;
  step: "Meditation";
}

/**
 * MeditationStep - General meditation timer step
 *
 * Features:
 * - Timer-based meditation session
 * - Progress visualization
 * - Start/complete callbacks
 * - Centering and breathing focus
 *
 * @param root0
 *
 * @param root0.step
 *
 * @param root0.duration
 *
 * @param root0.isActive
 *
 * @param root0.onStart
 *
 * @param root0.onComplete
 *
 * @param root0.className
 *
 * @example
 * ```tsx
 * <MeditationStep
 *   step="Meditation"
 *   duration={300}
 *   isActive={isMeditating}
 *   onStart={startMeditation}
 *   onComplete={completeMeditation}
 * />
 * ```
 *
 * @returns JSX.Element
 *
 */
export const MeditationStep: React.FC<MeditationStepProperties> = ({
  className,
  duration,
  isActive,
  onComplete,
  onStart,
  step,
}) => {
  return (
    <div className={cn("space-y-6 w-full", className)} data-step={step}>
      {/* Hero Section */}
      <Stack align="center" className="py-6 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-tracq-bright-blue" level="h3" size="xl">
            Meditation
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Take a moment to center yourself. Focus on your breathing and let go of any tension.
          </p>
        </Stack>
      </Stack>

      {/* Timer Section */}
      <Stack align="center" className="py-8 w-full" direction="col" gap={4}>
        <Timer
          duration={duration}
          isActive={isActive}
          onComplete={onComplete}
          onStart={onStart}
          showProgress
          size="lg"
        />
        {!isActive && (
          <p className="text-[#94A3B8] text-sm text-center">
            Click the timer to begin your meditation
          </p>
        )}
      </Stack>
    </div>
  );
};
